import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Skeleton,
  Stack,
  alpha,
} from '@mui/material';
import {
  People as PeopleIcon,
  AdminPanelSettings as RolesIcon,
  Webhook as IntegrationsIcon,
  Description as TemplatesIcon,
  Warehouse as WarehouseIcon,
  History as AuditIcon,
  Refresh,
  CheckCircle,
  Error as ErrorIcon,
  PauseCircle,
  Notifications as NotificationsIcon,
  PendingActions as PendingIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import api from '../services/api';
import PageHeader from '../components/ui/PageHeader';

interface ITSummary {
  integration_health: {
    total_services: number;
    active_services: number;
    error_services: number;
    inactive_services: number;
    services: Array<{ service_type: string; provider_name: string; status: string; message?: string }>;
  };
  recent_notification_failures: Array<{
    channel: string | null;
    provider_name: string | null;
    error_message: string | null;
    failed_at: string | null;
    event_type: string | null;
  }>;
  pending_template_approvals: number;
  admin_users_count: number;
  roles_count: number;
}

interface ShortcutCard {
  label: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  permission: string;
  color: string;
}

const ITDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<ITSummary | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/admin/dashboard/it-summary');
      setSummary(response.data);
    } catch (err: any) {
      setError(
        err?.response?.status === 403
          ? "You don't have permission to view the IT dashboard."
          : 'Failed to load system status. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const shortcuts: ShortcutCard[] = [
    { label: 'Users', description: 'Manage admin accounts', icon: <PeopleIcon />, path: '/admin/users', permission: 'admin.users.read', color: '#1976d2' },
    { label: 'Roles', description: 'Manage roles & permissions', icon: <RolesIcon />, path: '/admin/roles', permission: 'admin.roles.read', color: '#8e24aa' },
    { label: 'Integrations', description: 'Configure providers', icon: <IntegrationsIcon />, path: '/admin/integrations/config', permission: 'integrations.read', color: '#0288d1' },
    { label: 'Templates', description: 'Document & notification templates', icon: <TemplatesIcon />, path: '/admin/integrations/templates', permission: 'integrations.read', color: '#2e7d32' },
    { label: 'Warehouses', description: 'System configuration', icon: <WarehouseIcon />, path: '/admin/configuration/warehouses', permission: 'admin.system.config', color: '#ed6c02' },
    { label: 'Audit Logs', description: 'Review admin activity', icon: <AuditIcon />, path: '/analytics/audit-logs', permission: 'admin.audit.read', color: '#5d4037' },
  ].filter((s) => hasPermission(s.permission));

  const statusIcon = (status: string) => {
    if (status === 'active') return <CheckCircle sx={{ color: 'success.main', fontSize: 18 }} />;
    if (status === 'error') return <ErrorIcon sx={{ color: 'error.main', fontSize: 18 }} />;
    return <PauseCircle sx={{ color: 'text.disabled', fontSize: 18 }} />;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <PageHeader title="IT Dashboard" subtitle="System status, integrations, and quick actions" />
        <Tooltip title="Refresh">
          <IconButton onClick={fetchSummary} color="primary" disabled={loading}
            sx={{ bgcolor: 'white', border: '1px solid', borderColor: 'divider', boxShadow: 1 }}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="warning" sx={{ mb: 3 }} action={
          <Button color="inherit" size="small" onClick={fetchSummary}>Retry</Button>
        }>
          {error}
        </Alert>
      )}

      {/* Shortcut cards */}
      {shortcuts.length > 0 && (
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          {shortcuts.map((s) => (
            <Grid item xs={12} sm={6} md={4} lg={2} key={s.label}>
              <Card
                onClick={() => navigate(s.path)}
                sx={{
                  cursor: 'pointer', height: '100%',
                  border: `1px solid ${alpha(s.color, 0.15)}`,
                  boxShadow: `0 2px 12px ${alpha(s.color, 0.08)}`,
                  transition: 'all 0.2s',
                  '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 8px 20px ${alpha(s.color, 0.18)}` },
                }}
              >
                <CardContent sx={{ textAlign: 'center', py: 3 }}>
                  <Box sx={{
                    width: 44, height: 44, borderRadius: '50%', mx: 'auto', mb: 1.5,
                    bgcolor: alpha(s.color, 0.12), color: s.color,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {s.icon}
                  </Box>
                  <Typography variant="subtitle2" fontWeight={700}>{s.label}</Typography>
                  <Typography variant="caption" color="text.secondary">{s.description}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Integration health + pending approvals */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Card sx={{ boxShadow: '0 2px 16px rgba(0,0,0,0.06)', border: '1px solid', borderColor: 'divider', height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>Integration Health</Typography>
              {loading ? (
                <Skeleton variant="rounded" height={140} />
              ) : summary ? (
                <>
                  <Stack direction="row" spacing={3} mb={2}>
                    <Box>
                      <Typography variant="h5" fontWeight={700} color="success.main">
                        {summary.integration_health.active_services}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">Active</Typography>
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight={700} color="error.main">
                        {summary.integration_health.error_services}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">Error</Typography>
                    </Box>
                    <Box>
                      <Typography variant="h5" fontWeight={700} color="text.disabled">
                        {summary.integration_health.inactive_services}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">Inactive</Typography>
                    </Box>
                  </Stack>
                  {summary.integration_health.services.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Service</TableCell>
                            <TableCell>Provider</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {summary.integration_health.services.map((s, i) => (
                            <TableRow key={i}>
                              <TableCell sx={{ textTransform: 'capitalize' }}>{s.service_type}</TableCell>
                              <TableCell>{s.provider_name}</TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  {statusIcon(s.status)}
                                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{s.status}</Typography>
                                </Stack>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography variant="body2" color="text.secondary">No integrations configured yet.</Typography>
                  )}
                </>
              ) : null}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card
            onClick={() => hasPermission('integrations.read') && navigate('/admin/integrations/templates')}
            sx={{
              boxShadow: '0 2px 16px rgba(0,0,0,0.06)', border: '1px solid', borderColor: 'divider',
              height: '100%', cursor: hasPermission('integrations.read') ? 'pointer' : 'default',
            }}
          >
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <PendingIcon color="warning" />
                <Typography variant="h6" fontWeight={700}>Pending Approvals</Typography>
              </Stack>
              {loading ? (
                <Skeleton variant="text" width={80} height={60} />
              ) : (
                <>
                  <Typography variant="h3" fontWeight={800} color={summary && summary.pending_template_approvals > 0 ? 'warning.main' : 'text.primary'}>
                    {summary?.pending_template_approvals ?? 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Notification templates awaiting approval</Typography>
                </>
              )}
              <Box mt={2} pt={2} sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2" color="text.secondary">
                  {loading ? <Skeleton width={140} /> : `${summary?.admin_users_count ?? 0} active admin users`}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {loading ? <Skeleton width={100} /> : `${summary?.roles_count ?? 0} roles configured`}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent notification failures */}
      <Card sx={{ boxShadow: '0 2px 16px rgba(0,0,0,0.06)', border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" mb={2}>
            <NotificationsIcon color="error" />
            <Typography variant="h6" fontWeight={700}>Recent Notification Failures</Typography>
          </Stack>
          {loading ? (
            <Skeleton variant="rounded" height={160} />
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Channel</TableCell>
                    <TableCell>Event</TableCell>
                    <TableCell>Provider</TableCell>
                    <TableCell>Error</TableCell>
                    <TableCell>Failed At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!summary || summary.recent_notification_failures.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">No recent failures</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    summary.recent_notification_failures.map((f, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <Chip label={f.channel || 'unknown'} size="small" sx={{ textTransform: 'capitalize' }} />
                        </TableCell>
                        <TableCell sx={{ textTransform: 'capitalize' }}>{(f.event_type || '').toLowerCase().replace(/_/g, ' ')}</TableCell>
                        <TableCell>{f.provider_name || '-'}</TableCell>
                        <TableCell sx={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {f.error_message || '-'}
                        </TableCell>
                        <TableCell>{f.failed_at ? new Date(f.failed_at).toLocaleString() : '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ITDashboard;
